import React, { useMemo } from "react";

const ROLE_STYLES = {
  agent: "bg-violet-600/15 border-violet-500/30 text-violet-300",
  user: "bg-emerald-600/15 border-emerald-500/30 text-emerald-300",
};

function RoleBadge({ role }) {
  const cls =
    ROLE_STYLES[role] ||
    "bg-slate-600/15 border-slate-500/30 text-slate-300";
  const label =
    role === "agent" ? "AI Служител" : role === "user" ? "Длъжник" : String(role || "UNKNOWN");
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {label}
    </span>
  );
}

function toNumberOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickTurnTime(t) {
  const candidates = [
    t?.time_in_call_secs,
    t?.start_time_in_call_secs,
    t?.start_time_secs,
    t?.start_time,
    t?.start,
    t?.timestamp,
  ];

  for (const v of candidates) {
    const n = toNumberOrNull(v);
    if (n != null) return n;
  }
  return null;
}

function normalizeTimestampMode(mode) {
  const s = String(mode || "").trim().toLowerCase();
  if (s === "turn_end") return "turn_end";
  return "turn_start";
}

function pickTurnText(t) {
  const candidates = [
    t?.original_message,
    t?.message,
    t?.multivoice_message,
  ];

  for (const v of candidates) {
    const s = String(v ?? "").trim();
    if (s) return s;
  }
  return "";
}

export default function TranscriptDebts({
  convai = null,
  transcript = null,
  timestampMode = "turn_start",
}) {
  const parts = useMemo(() => {
    const arr = Array.isArray(transcript)
      ? transcript
      : Array.isArray(convai?.transcript)
        ? convai.transcript
        : [];

    const mode = normalizeTimestampMode(timestampMode);
    let prevBoundary = 0;
    const out = [];

    for (const t of arr) {
      if (!t || (t.role !== "agent" && t.role !== "user")) continue;

      const text = pickTurnText(t);
      const time = pickTurnTime(t);

      let start = time;
      if (mode === "turn_end" && time != null) {
        start = prevBoundary;
      }

      if (text) {
        out.push({
          role: t.role,
          text,
          start,
        });
      }

      if (mode === "turn_end" && time != null) {
        prevBoundary = Math.max(prevBoundary, time);
      }
    }

    return out;
  }, [convai, transcript, timestampMode]);

  const seek = (t) => {
    if (typeof t !== "number") return;
    window.dispatchEvent(new CustomEvent("seek-audio", { detail: { time: t } }));
  };

  if (!parts.length) {
    return (
      <div className="text-sm text-slate-400">
        Няма реплики.
      </div>
    );
  }

  return (
    <div className="w-full">
      {parts.map((s, i) => (
        <div
          key={i}
          className={`mb-2 p-3 rounded-md ${
            i % 2 === 0 ? "bg-[#0e1320]/60" : "bg-[#0e1320]/30"
          } border border-[#1f2638]`}
          onClick={() => (s.start != null ? seek(s.start) : null)}
          style={{ cursor: s.start != null ? "pointer" : "default" }}
          title={s.start != null ? `Скок до ${s.start.toFixed(2)}s` : undefined}
        >
          <div className="mb-1 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-200">
              <RoleBadge role={s.role} />
            </div>
            {s.start != null && (
              <span className="text-[11px] text-slate-500 tabular-nums">
                {s.start.toFixed(2)}s
              </span>
            )}
          </div>

          <div className="text-[15px] leading-relaxed text-slate-200 whitespace-pre-wrap">
            {s.text}
          </div>
        </div>
      ))}
    </div>
  );
}
